import { useEffect, useState } from "react";

import { Edit } from "lucide-react";

import LazySearchSelect from "../../../components/shared/LazySearchSelect.jsx";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { cn, getCharCount } from "../../../lib/utils.js";

const EditProjectDialog = ({ open, onOpenChange, project, onUpdate, users = [] }) => {
  const [editingProject, setEditingProject] = useState(null);

  useEffect(() => {
    if (project) {
      setEditingProject({
        ...project,
        startDate: project.startDate ? project.startDate.split("T")[0] : ""
      });
    }
  }, [project]);

  const isDescriptionOverLimit = editingProject ? getCharCount(editingProject.description) > 500 : false;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isDescriptionOverLimit) return;
    onUpdate(editingProject);
  };

  if (!editingProject) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-yale-blue-500" />
            Edit Project
          </DialogTitle>
          <DialogDescription>Update project information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="edit_name">Project Name *</Label>
            <Input
              id="edit_name"
              value={editingProject.name || ""}
              onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
              placeholder="Enter project name"
              required
            />
          </div>
          <div>
            <Label htmlFor="edit_description">Description</Label>
            <Textarea
              id="edit_description"
              value={editingProject.description || ""}
              onChange={(e) =>
                setEditingProject({
                  ...editingProject,
                  description: e.target.value
                })
              }
              className={cn(isDescriptionOverLimit && "border-red-500")}
              rows={3}
              placeholder="Project description"
            />
            <WordCount text={editingProject.description} />
          </div>
          <div>
            <Label htmlFor="edit_location">Location *</Label>
            <Input
              id="edit_location"
              value={editingProject.location || ""}
              onChange={(e) =>
                setEditingProject({
                  ...editingProject,
                  location: e.target.value
                })
              }
              placeholder="Project location"
              required
            />
          </div>
          <div>
            <Label htmlFor="edit_startDate">Start Date</Label>
            <Input
              id="edit_startDate"
              type="date"
              value={editingProject.startDate || ""}
              onChange={(e) =>
                setEditingProject({
                  ...editingProject,
                  startDate: e.target.value
                })
              }
            />
          </div>

          <div>
            <Label htmlFor="edit_estimated_budget">Estimated Budget (₹)</Label>
            <Input
              id="edit_estimated_budget"
              type="number"
              step="0.01"
              value={editingProject.estimatedBudget || ""}
              onChange={(e) =>
                setEditingProject({
                  ...editingProject,
                  estimatedBudget: e.target.value
                })
              }
              placeholder="Enter project budget"
            />
          </div>
          <div>
            <Label htmlFor="edit_status">Project Status</Label>
            <Select
              value={editingProject.status || "planning"}
              onValueChange={(val) => setEditingProject({ ...editingProject, status: val })}
            >
              <SelectTrigger id="edit_status" className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="planning">Planning</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="edit_pm">Project Manager</Label>
            <LazySearchSelect
              value={editingProject.projectManagerId}
              onChange={(val) => setEditingProject({ ...editingProject, projectManagerId: val })}
              placeholder="Select manager"
              searchPlaceholder="Search managers..."
              apiUrl="/users"
            />
          </div>
          <div className="flex justify-between gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="save" className="flex-1" disabled={isDescriptionOverLimit}>
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProjectDialog;
